import type {
  ApiResponse,
  ApiResponseEnvelope,
  PaginatedResponse,
} from '@/shared/api/types/api-response'
import type {
  AddTourPoisRequest,
  CreateTourPayload,
  ITour,
  ITourPoi,
  TourDetailDTO,
  TourDTO,
  TourListParams,
  TourRoutingRequest,
  TourUploadImageResponse,
  UpdateTourPayload,
  UpdateTourPoiColorRequest,
} from '@/shared/api/types/tour-types'
import { GW, toQueryString } from '@/shared/api/endpoints'
import {
  mapTourCreatePayloadToRequest,
  mapTourDetailDtoToTour,
  mapTourDtoToTour,
  mapTourUpdatePayloadToRequest,
} from '@/shared/api/mappers/tour-mapper'
import { buildLanguageHeaderConfig } from '@/shared/api/utils/language-header'
import { unwrapApiEnvelope } from '@/shared/api/utils/unwrapapi'
import { CSS_COLOR_NAME_SET, DEFAULT_CSS_COLOR_NAME } from '@/shared/constants/css-color-names'
import { request } from '@/shared/libs/request'

const TOURS_PATH = 'admin/tours'

function normalizeTourPoiColorName(color: string | null | undefined): string {
  const normalizedColor = color?.trim().toLowerCase()
  return normalizedColor && CSS_COLOR_NAME_SET.has(normalizedColor)
    ? normalizedColor
    : DEFAULT_CSS_COLOR_NAME
}

async function getToursByEndpoint(
  path: typeof TOURS_PATH | `${typeof TOURS_PATH}/all`,
  params: TourListParams,
): Promise<PaginatedResponse<ITour>> {
  const qs = toQueryString({
    current: params.current,
    pageSize: params.pageSize,
    ...(params.isActive !== undefined ? { isActive: params.isActive } : {}),
    ...(params.tenantDestinationId ? { tenantDestinationId: params.tenantDestinationId } : {}),
  })
  const response = await request.get<ApiResponseEnvelope<PaginatedResponse<TourDTO>>>(
    GW.packaging(`${path}?${qs}`),
    buildLanguageHeaderConfig(params.languageCode),
  )

  const pageData = unwrapApiEnvelope(response.data)

  return {
    data: pageData.data.map(mapTourDtoToTour),
    total: pageData.total,
    current: params.current,
    keyword: pageData.keyword ?? null,
    pageSize: params.pageSize,
  }
}

export async function getTours(params: TourListParams): Promise<PaginatedResponse<ITour>> {
  return getToursByEndpoint(TOURS_PATH, params)
}

export async function getToursSuperadmin(
  params: TourListParams,
): Promise<PaginatedResponse<ITour>> {
  return getToursByEndpoint(`${TOURS_PATH}/all`, params)
}

export async function getTourById(id: string, languageCode?: string): Promise<ApiResponse<ITour>> {
  const response = await request.get<ApiResponseEnvelope<TourDetailDTO>>(
    GW.packaging(`${TOURS_PATH}/${id}`),
    buildLanguageHeaderConfig(languageCode),
  )

  return {
    data: mapTourDetailDtoToTour(unwrapApiEnvelope(response.data)),
  }
}

export async function createTour(
  payload: CreateTourPayload,
  languageCode?: string,
): Promise<ApiResponse<ITour>> {
  const response = await request.post<ApiResponseEnvelope<TourDTO>>(
    GW.packaging(TOURS_PATH),
    mapTourCreatePayloadToRequest(payload),
    buildLanguageHeaderConfig(languageCode),
  )

  return {
    data: mapTourDtoToTour(unwrapApiEnvelope(response.data)),
    message: 'Tour created successfully',
  }
}

async function syncTourPois(
  id: string,
  nextPoiIds: string[],
  languageCode?: string,
): Promise<void> {
  const currentTour = await getTourById(id, languageCode)
  const currentPoiIds = (currentTour.data.pois ?? [])
    .slice()
    .sort((a, b) => a.order - b.order)
    .map((item) => item.poiId)

  const toAdd = nextPoiIds.filter((poiId) => !currentPoiIds.includes(poiId))
  const toRemove = currentPoiIds.filter((poiId) => !nextPoiIds.includes(poiId))

  if (toAdd.length > 0) {
    await addTourPois(id, toAdd)
  }

  if (toRemove.length > 0) {
    await removeTourPois(id, toRemove)
  }

  if (nextPoiIds.length > 0) {
    await request.put<ApiResponseEnvelope<null>>(GW.packaging(`${TOURS_PATH}/${id}/reorder-pois`), {
      poiIds: nextPoiIds,
    })
  }
}

export async function updateTour(
  id: string,
  payload: UpdateTourPayload,
): Promise<ApiResponse<null>> {
  await request.patch(GW.packaging(`${TOURS_PATH}/${id}`), mapTourUpdatePayloadToRequest(payload))
  return { data: null, message: 'Tour updated successfully' }
}

export async function deleteTour(id: string): Promise<ApiResponse<null>> {
  const response = await request.delete<ApiResponseEnvelope<null>>(
    GW.packaging(`${TOURS_PATH}/${id}`),
  )

  return {
    data: null,
    message: response.data?.message ?? 'Tour deleted successfully',
  }
}

export async function updateTourPoiOrder(
  id: string,
  pois: ITourPoi[],
): Promise<ApiResponse<ITour>> {
  const poiIds = pois
    .slice()
    .sort((a, b) => a.order - b.order)
    .map((item) => item.poiId)

  await request.put<ApiResponseEnvelope<null>>(GW.packaging(`${TOURS_PATH}/${id}/reorder-pois`), {
    poiIds,
  })

  const latestTour = await getTourById(id)
  return {
    data: latestTour.data,
    message: 'POI order updated successfully',
  }
}

export async function addTourPois(id: string, poiIds: string[]): Promise<ApiResponse<ITour>> {
  const payload: AddTourPoisRequest = {
    pois: poiIds.map((poiId) => ({
      poi_id: poiId,
      color: DEFAULT_CSS_COLOR_NAME,
    })),
  }

  const response = await request.post<ApiResponseEnvelope<TourDTO>>(
    GW.packaging(`${TOURS_PATH}/${id}/pois`),
    payload,
  )

  return {
    data: mapTourDtoToTour(unwrapApiEnvelope(response.data)),
    message: 'POIs added to tour successfully',
  }
}

export async function updateTourPoisBatch(
  id: string,
  pois: ITourPoi[],
): Promise<ApiResponse<null>> {
  const payload: AddTourPoisRequest = {
    pois: pois.map((item) => ({
      poi_id: item.poiId,
      color: normalizeTourPoiColorName(item.color),
      favorite_poi_rank: item.favoritePoiRank ?? null,
    })),
  }

  await request.patch<ApiResponseEnvelope<null>>(GW.packaging(`${TOURS_PATH}/${id}/pois`), payload)

  return { data: null, message: 'POI changes saved successfully' }
}

export async function removeTourPois(id: string, poiIds: string[]): Promise<ApiResponse<null>> {
  const payload: AddTourPoisRequest = { poiIds }

  const response = await request.delete<ApiResponseEnvelope<null>>(
    GW.packaging(`${TOURS_PATH}/${id}/pois`),
    { data: payload },
  )

  return {
    data: null,
    message: response.data.message ?? 'POIs removed from tour successfully',
  }
}

export async function removeTourPoi(id: string, poiId: string): Promise<ApiResponse<null>> {
  return removeTourPois(id, [poiId])
}

export async function saveTourPois(id: string, poiIds: string[]): Promise<ApiResponse<ITour>> {
  await syncTourPois(id, poiIds)
  const latestTour = await getTourById(id)

  return {
    data: latestTour.data,
    message: 'Tour POIs updated successfully',
  }
}

export async function updateTourPoiColor(
  id: string,
  poiId: string,
  color: string | null,
): Promise<ApiResponse<null>> {
  const payload: UpdateTourPoiColorRequest = {
    color,
  }

  await request.patch<ApiResponseEnvelope<null>>(
    GW.packaging(`${TOURS_PATH}/${id}/pois/${poiId}`),
    payload,
  )

  return {
    data: null,
    message: 'POI color updated successfully',
  }
}

export async function uploadTourImage(
  id: string,
  imageFile: File,
): Promise<ApiResponse<TourUploadImageResponse>> {
  const formData = new FormData()
  formData.append('image_file', imageFile)

  const response = await request.put<ApiResponseEnvelope<TourUploadImageResponse>>(
    GW.packaging(`${TOURS_PATH}/${id}/images`),
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  )

  return {
    data: unwrapApiEnvelope(response.data),
    message: 'Tour image uploaded successfully',
  }
}

export async function getTourRouting(
  id: string,
  payload: TourRoutingRequest,
): Promise<ApiResponse<Record<string, unknown>>> {
  const response = await request.post<ApiResponseEnvelope<Record<string, unknown>>>(
    GW.packaging(`${TOURS_PATH}/${id}/routing`),
    payload,
  )

  return {
    data: unwrapApiEnvelope(response.data),
    message: 'Tour routing generated successfully',
  }
}
